/** @file mesh.setVertices: replaces vertex positions (and uvs); a count change refits dependent data. */
import { z } from 'zod';
import type { MeshAtt } from '@pose2d/format/types';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import { checkMeshTopology } from './checkMeshTopology';
import { fitMeshToCount } from './fitMeshToCount';
import { meshDraft } from './meshDraft';
import { sameNumbers } from './sameNumbers';
import { writeChanged } from './writeChanged';

/** The stored positions of a mesh: rigid vertices or skinned bind vertices. */
const positionsOf = (g: MeshAtt['geometry']): number[] => (g.kind === 'rigid' ? g.vertices : g.bindVertices);
const same = (a: number, b: number): boolean => a === b;

/**
 * VALIDATION at `uvs` unless they are whole pairs. A mounted rigid mesh is edited like any other (R19-03): the compiler
 * fits its handle from these uvs and vertices (core meshPixelToLocal) and draws M · vertices (meshRecord).
 */
function refuseInput(uvs: readonly number[] | undefined): void {
  if (uvs !== undefined && uvs.length % 2 !== 0) throwError('VALIDATION', undefined, { path: 'uvs', hint: 'uvs hold 2 numbers (u, v) per vertex' });
}

/** mesh.setVertices: the uvs set the vertex count; rigid = slot-bone local px, skinned = bind px; skinned keeps its count. */
export const command = defineCommand({
  name: 'mesh.setVertices',
  description: "Replace a mesh's vertex positions (rigid: slot-bone local px, y up; skinned: bind-space px) and optionally its uvs (0..1 of the image, y down), which set the vertex count; a new count drops triangles and edges on removed vertices, clamps the hull, clips deform keys and removes mirror maps, a skinned mesh keeps its vertex count; a mounted mesh draws its new vertices through its mount.",
  input: z.object({ target: z.string().min(1), vertices: prim.numbers, uvs: z.array(prim.unit).min(6).exactOptional() }).strict(),
  apply(draft, input, ctx) {
    const { id, mesh } = meshDraft(draft, ctx, input.target);
    refuseInput(input.uvs);
    const g = mesh.geometry;
    if (sameNumbers(input.vertices, positionsOf(g)) && sameNumbers(input.uvs ?? mesh.uvs, mesh.uvs)) return;
    const before = mesh.uvs.length / 2;
    const count = (input.uvs ?? mesh.uvs).length / 2;
    if (count !== before) {
      if (g.kind === 'skinned') throwError('VALIDATION', undefined, { path: 'vertices', hint: `a skinned mesh keeps its ${before} vertices (W2-29): setBinding rigid, edit, then setBinding skinned and setWeights` });
      fitMeshToCount(draft, id, mesh, count, ctx);
    }
    // Same count: only the moved numbers are written (one patch each for a small edit, R17-01b).
    if (input.uvs !== undefined) {
      writeChanged(mesh.uvs, input.uvs, same, (next) => {
        mesh.uvs = next;
      });
    }
    writeChanged(positionsOf(g), input.vertices, same, (next) => {
      if (g.kind === 'rigid') g.vertices = next;
      else g.bindVertices = next;
    });
    checkMeshTopology(draft, mesh);
    draft.meta.updatedAt = ctx.now;
  },
});
